// Anita Week 10 / Day 3 — Light of the Explorer: Experiment, Data and Nature.
// Verbatim from FINAL Week 10 Part A (Final Quest) source-of-truth package.
// Staged on disk; Week 10 remains a locked placeholder until Days 4–5 are
// authored. Night Runner gate is NOT changed yet.

const day3 = {
  id: 'day-3',
  studentId: 'anita',
  weekId: 'week-10',
  dayId: 'day-3',
  status: 'active',
  title: 'Огонёк исследователя: опыт, данные и природа / Light of the Explorer: Experiment, Data and Nature',
  shortTitle: 'Огонёк исследователя / Light of the Explorer',
  theme: 'Анита повторяет измерения, движение, данные, графики, растения, животных и безопасное наблюдение природы. / Anita reviews measurement, motion, data, graphs, plants, animals and safe nature observation.',
  estimatedTimeLabel: '60–90 минут / 60–90 minutes',
  lessonTiming: { minMinutes: 60, maxMinutes: 90, breaks: 2 },
  bilingualMode: 'ru-primary-en-helper',
  readingLevel: 'grade-2',
  courseLayers: ['reading', 'math', 'quest-review', 'science', 'nature', 'memory-cards', 'creative', 'speech'],
  questMeta: { lightId: 'explorer-light', lightNameRu: 'Огонёк исследователя', lightNameEn: 'Light of the Explorer', questNode: 3, passportStamp: 'исследователь' },
  nimnyIntro:
    'Нимни открывает третью дверь. За ней светится Огонёк исследователя. Чтобы получить его, Анита вспоминает, как измерять, наблюдать, записывать данные, делать вывод и бережно относиться к природе. / Nimny opens the third door. Behind it shines the Light of the Explorer. To collect it, Anita reviews how to measure, observe, record data, make conclusions and treat nature safely.',
  goals: [
    'Повторить измерение, данные, опыт и вывод. / Review measurement, data, experiment and conclusion.',
    'Повторить движение, расстояние и таблицы. / Review motion, distance and tables.',
    'Повторить растения, животных, среду и связи в природе. / Review plants, animals, habitats and nature connections.',
    'Решать задачи на данные, графики, умножение и деление. / Solve data, graph, multiplication and division tasks.',
    'Сделать штамп Огонька исследователя. / Add the Light of the Explorer stamp.',
    'Объяснить родителю, что делает исследователь. / Explain what an explorer does.'
  ],
  vocabulary: [
    { term: 'измерить / measure', definition: 'узнать размер или количество. / find out size or quantity.' },
    { term: 'данные / data', definition: 'записанные результаты. / recorded results.' },
    { term: 'опыт / experiment', definition: 'проверка, чтобы узнать результат. / a check to learn the result.' },
    { term: 'вывод / conclusion', definition: 'что я понял после данных. / what I understood after the data.' },
    { term: 'движение / motion', definition: 'когда предмет меняет место. / when an object changes place.' },
    { term: 'расстояние / distance', definition: 'как далеко предмет прошёл. / how far an object went.' },
    { term: 'растение / plant', definition: 'живой организм, который растёт. / a living organism that grows.' },
    { term: 'животное / animal', definition: 'живой организм, который двигается и питается. / a living organism that moves and eats.' },
    { term: 'среда / habitat', definition: 'место, где живёт организм. / the place where an organism lives.' },
    { term: 'наблюдение / observation', definition: 'внимательное смотрение и запись. / careful watching and recording.' }
  ],
  onlineTasks: [
    { id: 'reading-words', type: 'text', title: 'Задание 1 — Слова исследователя / Explorer Words',
      prompt: 'Прочитай по-русски:\n\nизмерить\nданные\nопыт\nвывод\nдвижение\nрасстояние\nрастение\nживотное\nсреда\nнаблюдение',
      ruPrimaryText: 'Прочитай 10 русских слов исследователя.',
      enHelpText: 'Read the Russian explorer words first. English is only for help.',
      answer: 'Attempted Russian reading.' },
    { id: 'reading-sentences', type: 'text', title: 'Задание 2 — Фразы исследователя / Explorer Sentences',
      prompt: 'Прочитай по-русски:\n\nЯ измеряю и записываю данные.\nЯ сравниваю результаты.\nЯ делаю вывод.\nЯ наблюдаю природу безопасно.',
      ruPrimaryText: 'Прочитай 4 фразы исследователя.',
      enHelpText: 'I measure and record data. I compare results. I make a conclusion. I observe nature safely.',
      answer: 'Attempted Russian reading.' },
    { id: 'explorer-card-sort', type: 'classify', title: 'Задание 3 — Карточки исследователя / Explorer Cards',
      prompt: 'Разложи карточки по категориям.\n\nКатегории:\nИзмерение и данные\nДвижение\nПрирода\nБезопасность\n\nКарточки:\n1. Измерить длину карандаша.\n2. Записать 18 см, 25 см, 21 см.\n3. Машинка едет от старта к финишу.\n4. Сравнить расстояние.\n5. Растение имеет корень, стебель и листья.\n6. Животное живёт в среде.\n7. Не трогать незнакомый гриб.\n8. Смотреть на насекомое, не трогая его.\n9. Сделать график погоды.\n10. Записать вывод.\n11. Магнит притягивает скрепку.\n12. Наблюдать из окна или с родителем.',
      ruPrimaryText: 'Разложи 12 карточек: измерение и данные / движение / природа / безопасность.',
      enHelpText: 'Sort 12 cards: data / motion / nature / safety.',
      categories: [
        { id: 'data', label: 'Измерение и данные / Measurement and Data' },
        { id: 'motion', label: 'Движение / Motion' },
        { id: 'nature', label: 'Природа / Nature' },
        { id: 'safety', label: 'Безопасность / Safety' }
      ],
      items: [
        { id: 'e1', label: '1. Измерить длину карандаша.', category: 'data' },
        { id: 'e2', label: '2. Записать 18 см, 25 см, 21 см.', category: 'data' },
        { id: 'e3', label: '3. Машинка едет от старта к финишу.', category: 'motion' },
        { id: 'e4', label: '4. Сравнить расстояние.', category: 'motion' },
        { id: 'e5', label: '5. Растение имеет корень, стебель и листья.', category: 'nature' },
        { id: 'e6', label: '6. Животное живёт в среде.', category: 'nature' },
        { id: 'e7', label: '7. Не трогать незнакомый гриб.', category: 'safety' },
        { id: 'e8', label: '8. Смотреть на насекомое, не трогая его.', category: 'safety' },
        { id: 'e9', label: '9. Сделать график погоды.', category: 'data' },
        { id: 'e10', label: '10. Записать вывод.', category: 'data' },
        { id: 'e11', label: '11. Магнит притягивает скрепку.', category: 'motion' },
        { id: 'e12', label: '12. Наблюдать из окна или с родителем.', category: 'safety' }
      ],
      answer: 'Измерение и данные: 1, 2, 9, 10. Движение: 3, 4, 11. Природа: 5, 6. Безопасность: 7, 8, 12.' },
    { id: 'math-measure-data', type: 'text', title: 'Задание 4 — Таблица измерений / Measurement Data',
      prompt: 'Нимни измерила 4 предмета:\n\nкарандаш — 14 см\nкнига — 22 см\nложка — 16 см\nкоробка — 18 см\n\nОтветь:\n1. Какой предмет самый длинный?\n2. На сколько книга длиннее карандаша?\n3. Сколько всего см у ложки и коробки?\n4. Если 4 предмета разделить на 2 пары, сколько предметов в каждой паре?',
      ruPrimaryText: 'Реши 4 задачи по таблице измерений.',
      enHelpText: 'Solve 4 measurement-table problems.',
      answer: '1) Книга. 2) 22 − 14 = 8 см. 3) 16 + 18 = 34 см. 4) 4 ÷ 2 = 2.' },
    { id: 'math-motion-review', type: 'text', title: 'Задание 5 — Данные движения / Motion Data',
      prompt: 'Машинка проехала:\n\nПопытка 1 — 18 см\nПопытка 2 — 25 см\nПопытка 3 — 21 см\n\nОтветь:\n1. Какая попытка дальше всего?\n2. На сколько попытка 2 дальше попытки 1?\n3. Сколько всего см в попытках 1 и 3?\n4. Если 3 попытки повторить 4 раза, сколько попыток будет?',
      ruPrimaryText: 'Реши 4 задачи про данные движения.',
      enHelpText: 'Solve 4 motion-data problems.',
      answer: '1) Попытка 2. 2) 25 − 18 = 7 см. 3) 18 + 21 = 39 см. 4) 3 × 4 = 12 попыток.' },
    { id: 'math-nature-graph', type: 'text', title: 'Задание 6 — График природы / Nature Graph',
      prompt: 'В мини-атласе Нимни:\n\nрастения — 6 карточек\nживотные — 5 карточек\nсреды — 4 карточки\nсвязи природы — 3 карточки\n\nНарисуй квадратики или запиши числа:\nрастения: □ □ □ □ □ □\nживотные: □ □ □ □ □\nсреды: □ □ □ □\nсвязи: □ □ □\n\nОтветь:\n1. Каких карточек больше всего?\n2. Сколько всего карточек?\n3. На сколько растений больше, чем связей?\n4. Если 18 карточек разделить на 3 страницы, сколько на каждой?',
      ruPrimaryText: 'Нарисуй мини-график природы и реши 4 задачи.',
      enHelpText: 'Draw the mini nature graph and answer 4 questions.',
      answer: '1) Растений. 2) 6 + 5 + 4 + 3 = 18. 3) 6 − 3 = 3. 4) 18 ÷ 3 = 6.' },
    { id: 'safe-nature-choice', type: 'classify', title: 'Задание 7 — Безопасный исследователь / Safe Explorer',
      prompt: 'Разложи карточки.\n\nКатегории:\nМожно с родителем\nНельзя\n\nКарточки:\n1. Смотреть на растение и рисовать его.\n2. Сорвать незнакомый гриб.\n3. Наблюдать птицу из окна.\n4. Потрогать незнакомое насекомое.\n5. Записать данные в таблицу.\n6. Попробовать ягоду с куста.\n7. Нарисовать следы животного.\n8. Покормить дикое животное.\n9. Смотреть погоду из окна.\n10. Пойти одной искать жука.',
      ruPrimaryText: 'Разложи 10 карточек: можно с родителем / нельзя.',
      enHelpText: 'Sort 10 cards: allowed with parent / not allowed.',
      categories: [
        { id: 'parent', label: 'Можно с родителем / Allowed with parent' },
        { id: 'forbidden', label: 'Нельзя / Not allowed' }
      ],
      items: [
        { id: 'n1', label: '1. Смотреть на растение и рисовать его.', category: 'parent' },
        { id: 'n2', label: '2. Сорвать незнакомый гриб.', category: 'forbidden' },
        { id: 'n3', label: '3. Наблюдать птицу из окна.', category: 'parent' },
        { id: 'n4', label: '4. Потрогать незнакомое насекомое.', category: 'forbidden' },
        { id: 'n5', label: '5. Записать данные в таблицу.', category: 'parent' },
        { id: 'n6', label: '6. Попробовать ягоду с куста.', category: 'forbidden' },
        { id: 'n7', label: '7. Нарисовать следы животного.', category: 'parent' },
        { id: 'n8', label: '8. Покормить дикое животное.', category: 'forbidden' },
        { id: 'n9', label: '9. Смотреть погоду из окна.', category: 'parent' },
        { id: 'n10', label: '10. Пойти одной искать жука.', category: 'forbidden' }
      ],
      answer: 'Можно с родителем: 1, 3, 5, 7, 9. Нельзя: 2, 4, 6, 8, 10.' },
    { id: 'spelling-explorer', type: 'text', title: 'Задание 8 — Правописание / Spelling',
      prompt: 'Напиши:\n\nопыт\nданные\nвывод\nрастение\nсреда\n\nВставь буквы:\n\nоп_т\nданн_е\nвыв_д\nраст_ние\nср_да',
      ruPrimaryText: 'Напиши 5 слов и вставь пропущенные буквы.',
      enHelpText: 'Write 5 words and fill in the missing letters.',
      answer: 'опыт, данные, вывод, растение, среда.' },
    { id: 'creative-explorer-stamp', type: 'text', title: 'Задание 9 — Штамп огонька исследователя / Explorer Light Stamp',
      prompt: 'Нарисуй штамп Огонька исследователя.\n\nВ штампе должны быть:\n1. огонёк\n2. линейка или таблица\n3. растение или животное\n4. фраза:\n\nЯ наблюдаю и делаю вывод.',
      ruPrimaryText: 'Нарисуй штамп Огонька исследователя с 4 элементами.',
      enHelpText: 'Draw the Light of the Explorer stamp with 4 elements.',
      answer: 'Open-ended. Accept if stamp includes light, data/science symbol, nature symbol and phrase.' },
    { id: 'speech-explorer-light', type: 'text', title: 'Задание 10 — Объясни родителю / Explain to Parent',
      prompt: 'Скажи родителю по-русски:\n\nЯ измеряю.\nЯ записываю данные.\nЯ делаю вывод.\nЯ наблюдаю природу безопасно.',
      ruPrimaryText: 'Скажи родителю минимум 3 explorer ideas по-русски.',
      enHelpText: 'Tell parent at least 3 of the 4 explorer ideas in Russian.',
      answer: 'Accept if Anita says at least 3 of the 4 ideas with support.' }
  ],
  notebookInstructions: [
    'Если нет принтера:',
    '1. Напиши слова исследователя.',
    '2. Прочитай 4 фразы.',
    '3. Разложи карточки исследователя.',
    '4. Реши задачи с данными.',
    '5. Нарисуй мини-график природы.',
    '6. Разложи safety карточки.',
    '7. Нарисуй штамп Огонька исследователя.',
    '8. Объясни родителю, что делает исследователь.'
  ],
  printWorksheet: {
    title: 'Огонёк исследователя: опыт, данные и природа / Light of the Explorer',
    subtitle: 'Неделя 10 · День 3 / Week 10 · Day 3 · Quest Passport',
    pages: [
      { title: 'Quest Passport: Огонёк исследователя / Light of the Explorer',
        blocks: [
          { type: 'text', text: 'Имя: __________________________   Дата: __________________________' },
          { type: 'text', text: 'Миссия: Нимни открывает третью дверь финального квеста. Собери Огонёк исследователя.' },
          { type: 'text', text: 'Задание 1 — Слова исследователя. Прочитай и обведи:' },
          { type: 'list', items: [
            'измерить / measure',
            'данные / data',
            'опыт / experiment',
            'вывод / conclusion',
            'движение / motion',
            'расстояние / distance',
            'растение / plant',
            'животное / animal',
            'среда / habitat',
            'наблюдение / observation'
          ] },
          { type: 'text', text: 'Задание 2 — Фразы исследователя. Поставь галочку:' },
          { type: 'list', items: [
            '□ Я измеряю и записываю данные.',
            '□ Я сравниваю результаты.',
            '□ Я делаю вывод.',
            '□ Я наблюдаю природу безопасно.'
          ] }
        ] },
      { title: 'Карточки и данные / Cards and Data',
        blocks: [
          { type: 'text', text: 'Задание 3 — Карточки исследователя. Категории: И = измерение и данные, Д = движение, П = природа, Б = безопасность.' },
          { type: 'list', items: [
            '1. Измерить длину карандаша. ____',
            '2. Записать 18 см, 25 см, 21 см. ____',
            '3. Машинка едет от старта к финишу. ____',
            '4. Сравнить расстояние. ____',
            '5. Растение имеет корень, стебель и листья. ____',
            '6. Животное живёт в среде. ____',
            '7. Не трогать незнакомый гриб. ____',
            '8. Смотреть на насекомое, не трогая его. ____',
            '9. Сделать график погоды. ____',
            '10. Записать вывод. ____',
            '11. Магнит притягивает скрепку. ____',
            '12. Наблюдать из окна или с родителем. ____'
          ] },
          { type: 'text', text: 'Задание 4 — Таблица измерений. карандаш 14 см, книга 22 см, ложка 16 см, коробка 18 см.' },
          { type: 'list', items: [
            'Самый длинный: ____________________',
            '22 − 14 = ______',
            '16 + 18 = ______',
            '4 ÷ 2 = ______'
          ] },
          { type: 'text', text: 'Задание 5 — Данные движения. Попытка 1 — 18 см, Попытка 2 — 25 см, Попытка 3 — 21 см.' },
          { type: 'list', items: [
            'Дальше всего: ____________________',
            '25 − 18 = ______',
            '18 + 21 = ______',
            '3 × 4 = ______ попыток'
          ] }
        ] },
      { title: 'График, безопасность и штамп / Graph, Safety and Stamp',
        blocks: [
          { type: 'text', text: 'Задание 6 — График природы:' },
          { type: 'list', items: [
            'растения — 6: □ □ □ □ □ □',
            'животные — 5: □ □ □ □ □',
            'среды — 4: □ □ □ □',
            'связи — 3: □ □ □'
          ] },
          { type: 'list', items: [
            'Больше всего: ____________________',
            'Всего карточек: ______',
            'Растений больше связей на: ______',
            '18 ÷ 3 = ______'
          ] },
          { type: 'text', text: 'Задание 7 — Безопасный исследователь. М = можно с родителем, Н = нельзя.' },
          { type: 'list', items: [
            '1. Смотреть на растение и рисовать его. ____',
            '2. Сорвать незнакомый гриб. ____',
            '3. Наблюдать птицу из окна. ____',
            '4. Потрогать незнакомое насекомое. ____',
            '5. Записать данные в таблицу. ____',
            '6. Попробовать ягоду с куста. ____',
            '7. Нарисовать следы животного. ____',
            '8. Покормить дикое животное. ____',
            '9. Смотреть погоду из окна. ____',
            '10. Пойти одной искать жука. ____'
          ] },
          { type: 'text', text: 'Задание 8 — Правописание. Напиши и вставь буквы:' },
          { type: 'list', items: ['опыт   данные   вывод   растение   среда', 'оп_т   данн_е   выв_д   раст_ние   ср_да'] },
          { type: 'text', text: 'Задание 9 — Штамп огонька исследователя. Нарисуй:' },
          { type: 'list', items: [
            '□ огонёк',
            '□ линейка или таблица',
            '□ растение или животное',
            '□ фраза «Я наблюдаю и делаю вывод.»'
          ] },
          { type: 'text', text: 'Задание 10 — Объясни родителю. Поставь галочку:' },
          { type: 'list', items: [
            '□ Я измеряю.',
            '□ Я записываю данные.',
            '□ Я делаю вывод.',
            '□ Я наблюдаю природу безопасно.'
          ] },
          { type: 'text', text: 'Parent Check: □ Прочитала слова  □ Прочитала фразы  □ Разложила карточки  □ Решила math-задачи  □ Проверила safety cards  □ Сделала штамп  □ Объяснила родителю.' },
          { type: 'text', text: 'Notebook Mode — если нет принтера, сделай эти задания в тетради.' }
        ] }
    ]
  },
  parentGuide: {
    goal: 'Анита собирает Огонёк исследователя и повторяет измерения, данные, выводы, природу и безопасное наблюдение.',
    mathGoal: 'Сложение, вычитание, умножение, деление, таблицы данных и мини-график.',
    russianGoal: 'Прочитать слова измерить, данные, опыт, вывод, движение, расстояние, растение, животное, среда, наблюдение.',
    readingWritingGoal: 'Прочитать 4 фразы, написать 5 слов, подписать explorer stamp.',
    practicalGoal: 'Отличить измерение/движение/природу/безопасность и объяснить роль исследователя.',
    parentTheoryCard: {
      title: 'Как повторять науку и природу через квест',
      purpose: 'Помочь родителю соединить Week 3 и Week 8 в одну лёгкую финальную миссию.',
      keyMessage: 'Исследователь наблюдает, записывает данные, делает вывод и действует безопасно.',
      parentScript: [
        '«Сегодня ты собираешь Огонёк исследователя.»',
        '«Мы вспоминаем: измерить, записать, сравнить, сделать вывод.»',
        '«В природе главное — наблюдать безопасно.»'
      ],
      practiceSteps: [
        'Прочитать слова.',
        'Разложить карточки.',
        'Решить таблицы данных.',
        'Сделать штамп.',
        'Попросить Аниту объяснить, что делает исследователь.'
      ],
      doNotSay: [
        'Не требовать настоящего опыта сейчас.',
        'Не просить трогать растения, грибы, насекомых или животных.',
        'Не превращать квест в экзамен.'
      ],
      paperOnlyNotes: [
        'Можно делать в тетради.',
        'Рисунок может быть простым.',
        'Наблюдать можно из окна или по памяти.'
      ]
    },
    whatThisLessonTeaches: 'Анита повторяет scientific thinking: наблюдение → данные → вывод → безопасность.',
    keyMessage: 'Я могу быть исследователем: наблюдать, записывать, сравнивать и делать вывод.',
    howToExplain: 'Скажите: «Исследователь не просто смотрит. Он замечает, записывает и объясняет, что понял.»',
    exampleDialogue:
      'Parent: Что делает исследователь?\nChild: Наблюдает.\nParent: Что он записывает?\nChild: Данные.\nParent: Что он делает потом?\nChild: Вывод.',
    readingSupport: 'Слова «наблюдение», «расстояние», «животное» читать по частям.',
    russianSpeechGoal: 'Анита говорит минимум 3 explorer ideas по-русски.',
    mathAnswerKey:
      'explorer-card-sort: Измерение и данные: 1,2,9,10. Движение: 3,4,11. Природа: 5,6. Безопасность: 7,8,12.\nmath-measure-data: 1) Книга. 2) 8 см. 3) 34 см. 4) 2.\nmath-motion-review: 1) Попытка 2. 2) 7 см. 3) 39 см. 4) 12.\nmath-nature-graph: 1) Растений. 2) 18. 3) 3. 4) 6.\nsafe-nature-choice: Можно с родителем: 1,3,5,7,9. Нельзя: 2,4,6,8,10.',
    spellingAnswerKey: 'опыт\nданные\nвывод\nрастение\nсреда',
    answerKey: [
      { taskId: 'reading-words', answer: 'Попытка чтения.' },
      { taskId: 'reading-sentences', answer: 'Попытка чтения.' },
      { taskId: 'explorer-card-sort', answer: 'Измерение и данные: 1,2,9,10; Движение: 3,4,11; Природа: 5,6; Безопасность: 7,8,12.' },
      { taskId: 'math-measure-data', answer: 'книга; 8; 34; 2.' },
      { taskId: 'math-motion-review', answer: 'попытка 2; 7; 39; 12.' },
      { taskId: 'math-nature-graph', answer: 'растений; 18; 3; 6.' },
      { taskId: 'safe-nature-choice', answer: 'parent: 1,3,5,7,9; not allowed: 2,4,6,8,10.' },
      { taskId: 'spelling-explorer', answer: 'опыт, данные, вывод, растение, среда.' },
      { taskId: 'creative-explorer-stamp', answer: 'stamp with light, science/data symbol, nature symbol and phrase.' },
      { taskId: 'speech-explorer-light', answer: 'explanation attempted.' }
    ],
    check: [
      'Анита прочитала слова и фразы.',
      'Анита разложила карточки.',
      'Анита решила data tasks.',
      'Анита проверила safety cards.',
      'Анита сделала штамп.',
      'Анита объяснила роль исследователя.'
    ],
    spellingCheck: 'Проверить опыт, данные, вывод, растение, среда.',
    readingCheck: 'Засчитывать попытку русского чтения.',
    practicalCheck: 'Анита понимает: исследователь наблюдает безопасно и делает вывод.',
    drawingCriteria: 'Штамп содержит огонёк, data/science symbol, nature symbol and phrase.',
    commonMistakes: [
      '«Исследователь трогает всё.» Правильно: «Исследователь наблюдает безопасно.»',
      '«Данные — это просто числа.» Правильно: «Данные — записанные результаты.»',
      '«Если вывод короткий, он плохой.» Правильно: «Хороший вывод может быть коротким.»'
    ],
    discussionPrompts: [
      'Что такое данные?',
      'Что такое вывод?',
      'Почему в природе важно быть осторожной?'
    ],
    ifEasy: 'Попросить Аниту придумать свой mini data table.',
    ifDifficult: 'Оставить только карточки измерение/природа/безопасность.',
    rewardGuidance: 'Базовая награда — 3 нима. Бонус за ясный вывод или аккуратный штамп.',
    tokenReward: 'Нимы — это призовые токены курса, а не оплата.',
    parentOnlyNotes: 'Не проводить новые опыты, если нет времени. Можно повторить по карточкам.',
    safetyNotes: 'Не трогать незнакомые растения, грибы, насекомых или животных. Не есть найденное. Не выходить одной.'
  },
  reward: { unit: 'nim', unitName: 'ним', base: 3, max: 5, label: 'Награда за миссию', display: '3 нима',
    bonuses: [
      { id: 'clear-conclusion', label: 'Бонус за ясный вывод по-русски', amount: 1 },
      { id: 'neat-explorer-stamp', label: 'Бонус за аккуратный штамп исследователя', amount: 1 }
    ] },
  completionRequirements: {
    requiredTaskIds: ['reading-words', 'reading-sentences', 'explorer-card-sort', 'math-measure-data', 'math-motion-review', 'math-nature-graph', 'safe-nature-choice', 'spelling-explorer', 'creative-explorer-stamp', 'speech-explorer-light'],
    allowNotebookCompletion: true
  },
  nextLessonId: 'anita-week-10-day-4'
};

export default day3;
